import InfoIcon from "@assets/icons/info.svg";
import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { useI18n } from "@core/i18n/i18n";
import { localeManager, registerManager, userManager } from "@core/service-container";
import { MedicalProfession } from "@domain/localization/country";
import styled from "@emotion/styled";
import { Button } from "@ui/components/buttons/button";
import { NetworkError } from "@ui/components/common/error";
import { ErrorMessage } from "@ui/components/common/error-message";
import { TextInput } from "@ui/components/inputs/text-input";
import { LoadingContainer } from "@ui/components/common/loading-container";
import { Tooltip } from "@ui/components/common/tooltip";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { useCountryConfig } from "@ui/pages/patient-details/hooks/use-country-config";
import { useRegistrationCountryUserConfig } from "@ui/pages/register/steps/hooks/use-registration-country-user-config";
import { physioMeMobileBreakpoint } from "@ui/style";
import { colors } from "@ui/style/colors";
import { isNotEmpty, isNotNil } from "@utils/assert";
import { useObservable } from "micro-observables";
import { Dropdown } from "primereact/dropdown";
import React, { useCallback } from "react";
import { Controller, useForm } from "react-hook-form";

interface RegisterProfessionalInfoFormData {
	postalCode: string;
	cityOfPractice: string;
	medicalProfession: MedicalProfession;
	idType: string;
	idValue: string;
}

interface RegisterProfessionalInfoProps {
	onSuccess: () => void;
}

const DEFAULT_REQUIRED_WORDING = "global.form.field.requiredError";

export const ProfessionalInfoStep = ({
	onSuccess,
}: // eslint-disable-next-line sonarjs/cognitive-complexity
RegisterProfessionalInfoProps): JSX.Element => {
	const { format } = useI18n();
	const { isMobile } = useDimensions();
	const { countryConfig } = useCountryConfig();
	const countryCode = useObservable(localeManager.countryCode);
	const user = useObservable(userManager.user);

	const { control, handleSubmit, watch, setValue, clearErrors } = useForm<RegisterProfessionalInfoFormData>({
		defaultValues: {
			cityOfPractice: "",
			postalCode: "",
			idType: undefined,
			idValue: undefined,
			medicalProfession: undefined,
		},
	});

	const selectedIdName = watch("idType");
	const {
		idTypeOptions,
		medicalProfessionOptions,
		isError,
		isLoading,
		isEmpty,
		selectedIdProps,
		selectedPostalCodeTemplate,
		refresh,
	} = useRegistrationCountryUserConfig(selectedIdName, value => setValue("idType", value));

	const [sendRegistrationData, isSubmitLoading] = useAsyncOperation(
		useCallback(
			async (data: RegisterProfessionalInfoFormData) => {
				if (!isNotNil(user)) {
					throw new Error("User is not loaded");
				}
				try {
					await registerManager.validateProfessionalInformation({
						...data,
						firstName: user.firstName,
						lastName: user.lastName,
						professionalId: {
							value: data.idValue,
							countryCode,
							name: data.idType,
						},
					});
					onSuccess();
				} catch (e) {
					console.error("Failed to send registration data", e);
					throw e;
				}
			},
			[countryCode, onSuccess, user],
		),
		{},
	);

	let content: JSX.Element;
	if (isLoading) {
		content = <LoadingContainer />;
	} else if (isError) {
		content = <NetworkError onRefresh={refresh} />;
	} else {
		content = (
			<Form onSubmit={handleSubmit(sendRegistrationData)}>
				<Header className="p-size-l-bold">{format("register.professionalInfo.header")}</Header>

				<TwoColumnLayout>
					<Column>
						<Controller
							name="postalCode"
							control={control}
							rules={{
								required: format(DEFAULT_REQUIRED_WORDING),
								pattern: new RegExp(selectedPostalCodeTemplate?.validationRegex ?? ""),
							}}
							render={({ field, fieldState }) => (
								<>
									<Label className="p-size-m">{format("register.professionalInfo.postalCode.label")}</Label>
									<TextInput
										{...field}
										error={!!fieldState.error}
										placeholder={
											selectedPostalCodeTemplate?.placeholder ??
											format("register.professionalInfo.postalCode.placeholder")
										}
									/>
									<StyledErrorMessage>{fieldState.error?.message}</StyledErrorMessage>
									<StyledErrorMessage color={colors.rejection} hidden={!(fieldState.error && isNotEmpty(field.value))}>
										{format("register.professionalInfo.postalCode.errorText")}
									</StyledErrorMessage>
								</>
							)}
						/>
					</Column>
					<Column>
						<Controller
							name="cityOfPractice"
							control={control}
							rules={{
								required: format(DEFAULT_REQUIRED_WORDING),
							}}
							render={({ field, fieldState }) => (
								<>
									<Label className="p-size-m">{format("register.professionalInfo.city.label")}</Label>
									<TextInput
										{...field}
										error={!!fieldState.error}
										placeholder={format("register.professionalInfo.city.placeholder")}
									/>
									<StyledErrorMessage>{fieldState.error?.message}</StyledErrorMessage>
								</>
							)}
						/>
					</Column>
				</TwoColumnLayout>

				<FormField>
					<Controller
						name="medicalProfession"
						control={control}
						rules={{
							required: format("global.form.dropdown.requiredError"),
						}}
						render={({ field, fieldState }) => (
							<>
								<Label className="p-size-m">{format("register.professionalInfo.medicalExpertise.label")}</Label>
								<Dropdown
									options={medicalProfessionOptions}
									optionLabel="label"
									optionValue="value"
									placeholder={format("global.form.dropdown.placeholder")}
									className={fieldState.error ? "p-invalid" : undefined}
									{...field}
								/>
								<StyledErrorMessage>{fieldState.error?.message}</StyledErrorMessage>
							</>
						)}
					/>
				</FormField>

				{!isEmpty && (
					<TwoColumnLayout>
						<Column>
							<Controller
								name="idType"
								control={control}
								rules={{
									required: format("global.form.dropdown.requiredError"),
									onChange: () => {
										clearErrors("idValue");
										setValue("idValue", "");
									},
								}}
								render={({ field, fieldState }) => (
									<>
										<Label className="p-size-m">{format("register.professionalInfo.idType.label")}</Label>
										<Dropdown
											options={idTypeOptions}
											optionLabel="label"
											optionValue="value"
											placeholder={format("global.form.dropdown.placeholder")}
											className={fieldState.error ? "p-invalid" : undefined}
											{...field}
										/>
										<StyledErrorMessage>{fieldState.error?.message}</StyledErrorMessage>
									</>
								)}
							/>
						</Column>

						{isNotNil(selectedIdProps) && (
							<Column>
								<Controller
									name="idValue"
									control={control}
									rules={{
										required: true,
										pattern: new RegExp(selectedIdProps.regex),
									}}
									render={({ field, fieldState }) => (
										<>
											<LabelWithTooltip className="p-size-m">
												{selectedIdProps.label}
												<Tooltip
													placement="auto"
													interactive
													content={
														<div>
															{selectedIdProps.description}
															<br />
															<br />
															{format("register.professionalInfo.idValue.tooltip.contactSupport", {
																link: {
																	onClick: () => window.open(`mailto:${countryConfig?.customerSupportEmail}`),
																	className: "underline",
																},
															})}
														</div>
													}
													maxWidth={isMobile ? 250 : 400}
												>
													<div>
														<InfoIcon
															style={{ cursor: "help" }}
															className="info-icon-id-value"
															width={16}
															height={16}
															color={colors.grey}
														/>
													</div>
												</Tooltip>
											</LabelWithTooltip>

											<TextInput
												{...field}
												error={!!fieldState.error}
												placeholder={selectedIdProps.placeholder}
												maxLength={selectedIdProps.maxLength}
											/>

											<HelpText color={fieldState.error ? colors.rejection : colors.grey}>
												{selectedIdProps.helpText}
											</HelpText>
										</>
									)}
								/>
							</Column>
						)}
					</TwoColumnLayout>
				)}
				<StyledButton label={format("register.professionalInfo.submitButton")} type="submit" loading={isSubmitLoading} />
			</Form>
		);
	}

	return content;
};

const HelpText = styled.div<{ color: string }>`
	margin-top: 8px;
	color: ${({ color }) => color};
`;

const Label = styled.label`
	margin-bottom: 8px;
`;

const LabelWithTooltip = styled.div`
	display: flex;
	place-items: center;
	gap: 8px;
	margin-bottom: 8px;
`;

const Form = styled.form`
	max-width: 100%;
	display: flex;
	flex-direction: column;
	width: 684px;
`;

const Header = styled.div`
	margin-bottom: 24px;
`;

const StyledErrorMessage = styled(ErrorMessage)`
	margin-top: 8px;
`;

const FormField = styled.div`
	display: grid;
	margin-bottom: 16px;
`;

const TwoColumnLayout = styled(FormField)`
	grid-template-columns: 1fr 1fr;
	gap: 16px;
	align-items: start;
	@media ${physioMeMobileBreakpoint} {
		grid-template-columns: 1fr;
	}
`;

const Column = styled.div`
	display: grid;
`;

const StyledButton = styled(Button)`
	margin-top: 24px;
`;
